import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { useDeclareCareOutcome } from '@/lib/disruptions/use-disruptions';
import type { CareOutcomeKind } from '@/lib/disruptions/types';
import type { CareDeskRow } from './care-desk-model';

// The care desk's formal outcome dialog (feat/care-desk K6, NEX-458) — the
// hotel-desk OutcomeDialog grammar for the CARE ladder's terminal rungs. Both
// paths end the group's card work on this desk (a real cases write with
// operator + timestamp — not a UI tag), so the copy is consequence-first and
// each path names its own confirm. Cancel is the SAFE first button; a failed
// write keeps the dialog open — "nothing was saved".

export function CareOutcomeDialog({
  desk,
  disruptionUrn,
  initialPath,
  onClose,
}: {
  desk: CareDeskRow;
  disruptionUrn: string;
  initialPath?: CareOutcomeKind;
  onClose: () => void;
}) {
  const { t } = useTranslation('care');
  const { row } = desk;
  const [path, setPath] = useState<CareOutcomeKind>(initialPath ?? 'COUNTER_VOUCHER');
  const [note, setNote] = useState('');
  const [failed, setFailed] = useState(false);
  const declare = useDeclareCareOutcome(disruptionUrn);

  const confirm = async (): Promise<void> => {
    setFailed(false);
    try {
      await declare.mutateAsync({
        subCaseUrn: row.subCaseUrn,
        caseUrn: row.caseUrn,
        outcome: path,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onClose();
    } catch {
      // The dialog STAYS OPEN — nothing was saved; the operator retries explicitly.
      setFailed(true);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-md" data-testid="care-outcome-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('outcomeDialog.title')} <span className="font-mono">{row.pnrLabel}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <PathOption
            selected={path === 'COUNTER_VOUCHER'}
            onSelect={() => setPath('COUNTER_VOUCHER')}
            title={t('outcomeDialog.counterVoucher.title')}
            body={t('outcomeDialog.counterVoucher.body')}
            testId="care-outcome-counter-voucher"
          />
          <PathOption
            selected={path === 'HANDLED_OUTSIDE'}
            onSelect={() => setPath('HANDLED_OUTSIDE')}
            title={t('outcomeDialog.handledOutside.title')}
            body={t('outcomeDialog.handledOutside.body')}
            tone="destructive"
            testId="care-outcome-handled-outside"
          />
          <label className="block text-xs text-muted-foreground">
            {t('outcomeDialog.note.label')}
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
              placeholder={
                path === 'COUNTER_VOUCHER'
                  ? t('outcomeDialog.note.placeholderCounter')
                  : t('outcomeDialog.note.placeholderOutside')
              }
              data-testid="care-outcome-note"
            />
          </label>
        </div>

        {failed ? (
          <p className="text-xs text-destructive" role="alert" data-testid="care-outcome-error">
            {t('outcomeDialog.error')}
          </p>
        ) : null}

        {/* Cancel first — the safe path leads. */}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={declare.isPending}>
            {t('common:common.cancel')}
          </Button>
          <Button
            variant={path === 'HANDLED_OUTSIDE' ? 'destructive' : 'default'}
            onClick={() => void confirm()}
            disabled={declare.isPending}
            data-testid="care-outcome-dialog-confirm"
          >
            {declare.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
            {path === 'COUNTER_VOUCHER'
              ? t('outcomeDialog.confirmCounter')
              : t('outcomeDialog.confirmOutside')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PathOption({
  selected,
  onSelect,
  title,
  body,
  tone,
  testId,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  body: string;
  tone?: 'destructive';
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      data-testid={testId}
      className={cn(
        'w-full rounded-md border p-2.5 text-left transition-colors',
        selected
          ? tone === 'destructive'
            ? 'border-rose-400 bg-rose-50 dark:border-rose-700 dark:bg-rose-950/30'
            : 'border-primary bg-primary/5'
          : 'border-border hover:bg-muted/50',
      )}
    >
      <p
        className={cn(
          'text-sm font-semibold',
          tone === 'destructive' && 'text-rose-700 dark:text-rose-300',
        )}
      >
        {title}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">{body}</p>
    </button>
  );
}
